import { t as uiText } from '../i18n';
// src/pages/AdminLegacyRedirect.tsx
import React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../lib/firebase';
import {
  adminOrgPath,
  fetchUserCompanyIds,
  isAdminSection,
  pickCompanyIdFromUserList,
  readLastAdminCompanyId,
  ADMIN_NO_COMPANY_PLACEHOLDER,
  type AdminSection,
} from '../utils/adminRoutes';

type Props = {
  /** `/admin` 用（params に section が無い） */
  defaultSection?: AdminSection;
};

const AdminLegacyRedirect: React.FC<Props> = ({ defaultSection = 'dashboard' }) => {
  const [user, authLoading] = useAuthState(auth as any);
  const { legacySection } = useParams<{ legacySection?: string }>();
  const [target, setTarget] = React.useState<string | null>(null);
  const [error, setError] = React.useState<'bad_section' | null>(null);

  const section: string = legacySection ?? defaultSection;

  React.useEffect(() => {
    if (!isAdminSection(section)) {
      setError('bad_section');
      return;
    }
    setError(null);
  }, [section]);

  React.useEffect(() => {
    if (error === 'bad_section') return;
    if (!isAdminSection(section)) return;
    if (authLoading) return;

    if (!user?.uid) {
      setTarget('/sys/login');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const ids = await fetchUserCompanyIds(user.uid);
        const pick = pickCompanyIdFromUserList(ids, readLastAdminCompanyId());
        const companyId = pick ?? ADMIN_NO_COMPANY_PLACEHOLDER;
        if (!cancelled) setTarget(adminOrgPath(companyId, section));
      } catch (e) {
        console.warn('[AdminLegacyRedirect] failed', e);
        if (!cancelled) setTarget(adminOrgPath(ADMIN_NO_COMPANY_PLACEHOLDER, section));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.uid, authLoading, section, error]);

  if (error === 'bad_section') {
    return (
      <div style={{ padding: 24, fontFamily: 'system-ui', color: '#333' }}>
        <div style={{ fontWeight: 800, marginBottom: 8 }}>404</div>
        <div style={{ fontSize: 14 }}>{uiText("Page not found.")}</div>
      </div>
    );
  }

  if (!target) {
    return (
      <div style={{ padding: 24, fontFamily: 'system-ui', color: '#666', fontSize: 14 }}>
        {uiText("Loading now...")}
      </div>
    );
  }

  return <Navigate to={target} replace />;
};

export default AdminLegacyRedirect;
